import { useAuth } from '@/loaders/Auth'
import { webSocketManager } from '@/services/WebSocketManager'
import { useStytchOrganization } from '@stytch/react/b2b'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'

import type { WebSocketMessage } from '@/services/WebSocketManager'
import type { MaterializedGrid, TargetedAreas } from 'honeygrid-types'

const targetedAreasKey = ['targetedAreas'] as const

export function useTargetedAreaWebsocket() {
	const { gridId = '' } = useParams()
	const { organization } = useStytchOrganization()
	const [searchParams] = useSearchParams()
	// When on the /add route, gridId param will be "add", so prioritize query param
	const gridDoId =
		gridId === 'add' ? searchParams.get('gridId') || '' : gridId || searchParams.get('gridId') || ''
	const queryClient = useQueryClient()
	const { getToken } = useAuth()

	const { data, isFetching, isSuccess } = useQuery<MaterializedGrid>({
		queryKey: [...targetedAreasKey, organization?.organization_id, gridDoId],
		queryFn: async () => {
			if (!organization?.organization_id) throw new Error('No organization id available')
			const token = await getToken()
			if (!token) throw new Error('No auth token available')

			// Initial fetch to get the first state
			await webSocketManager.getConnection(organization?.organization_id, gridDoId, token)
			return new Promise<MaterializedGrid>((resolve, reject) => {
				const handler = (message: WebSocketMessage) => {
					if (message.type === 'grid') {
						queryClient.setQueryData<MaterializedGrid>(
							[...targetedAreasKey, organization?.organization_id, gridDoId],
							message.data
						)
						resolve(message.data)
					} else if (message.type === 'error') {
						console.error('WebSocket error:', message.message)
						reject(new Error(message.message))
					}
				}
				webSocketManager.addMessageHandler(organization?.organization_id, gridDoId, handler)
			})
		},
		enabled: !!organization?.organization_id && !!gridDoId && !!getToken,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	})

	const { mutateAsync: updateTargetedAreas } = useMutation({
		mutationFn: async (updates: TargetedAreas) => {
			console.log('updating targeted areas', updates)
			const token = await getToken()
			if (!token) throw new Error('No auth token available')
			if (!organization?.organization_id) throw new Error('No organization id available')
			await webSocketManager.getConnection(organization?.organization_id, gridDoId, token)
			await webSocketManager.sendMessage(organization?.organization_id, gridDoId, token, {
				type: 'updateTargetedAreas',
				targetedArea: updates,
				gridId: gridDoId,
				workspaceId: organization?.organization_id,
			})
		},
	})

	return { data, isFetching, isSuccess, updateTargetedAreas }
}
